import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import {
  Send,
  MessageSquare,
  Loader2,
  ArrowLeft,
  User,
  Store,
  Search,
  Sparkles,
  Shield,
  MoreHorizontal,
  CheckCheck,
  Bot,
  Smile,
  Paperclip,
  FileText,
  Download,
  X,
} from 'lucide-react';

// --- TYPES ---
type Message = {
  id: string;
  conversation_id: string;
  sender_id: string;
  receiver_id?: string;
  content: string;
  created_at: string;
  is_read?: boolean;
  attachment_path?: string | null;
  attachment_name?: string | null;
  attachment_mime_type?: string | null;
  attachment_size?: number | null;
};

type Conversation = {
  id: string;
  traveler_id: string;
  business_id: string;
  last_message: string;
  updated_at: string;
  name?: string;
  business?: { name: string; id?: string; owner_id?: string; image_url?: string };
  business_owner_id?: string;
  traveler_profiles?: { email: string; id: string };
  traveler_email?: string;
};

interface ChatProps {
  userType?: 'traveler' | 'business';
  userId: string;
  initialContactId?: string | null;
  businessId?: string | null;
}

const formatShortDate = (dateString?: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('fr-CA', {
    month: 'short',
    day: 'numeric',
  });
};

const formatTime = (dateString?: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('fr-CA', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getDayLabel = (dateString?: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (isSameDay(date, today)) return "Aujourd'hui";
  if (isSameDay(date, yesterday)) return 'Hier';

  return date.toLocaleDateString('fr-CA', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
};

const getInitials = (name?: string) => {
  if (!name) return 'FY';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
};

const CHAT_ATTACHMENT_BUCKET = 'chat-attachments';
const CHAT_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
const CHAT_ATTACHMENT_TYPES = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

const CHAT_EMOJIS = [
  '😀', '😂', '😊', '😍', '🥰', '😎', '🤩', '🤔',
  '👍', '👏', '🙏', '💪', '👌', '✅', '🎉', '✨',
  '❤️', '💙', '💚', '🔥', '⭐', '📌', '📅', '📎',
  '🏨', '🍽️', '🎁', '🚗', '✈️', '🌟', '🙂', '😉',
];

const sanitizeFileName = (name: string) =>
  name
    .normalize('NFKD')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 120) || 'document';

const humanFileSize = (bytes?: number | null) => {
  const value = Number(bytes || 0);
  if (!value) return '';
  if (value < 1024) return `${value} o`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} Ko`;
  return `${(value / (1024 * 1024)).toFixed(1)} Mo`;
};

const ChatSystem = ({ userType = 'traveler', userId, initialContactId, businessId }: ChatProps) => {
  const [items, setItems] = useState<Conversation[]>([]);
  const [activeItem, setActiveItem] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [effectiveUserId, setEffectiveUserId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isCompact, setIsCompact] = useState(false);
  const [composerFocused, setComposerFocused] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState<File | null>(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [attachmentUrls, setAttachmentUrls] = useState<Record<string, string>>({});

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const openedOnceRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const updateCompact = (observedWidth?: number) => {
      const width = observedWidth || node.getBoundingClientRect().width || 0;
      const visualViewportWidth = window.visualViewport?.width || 0;
      const layoutViewportWidth =
        window.innerWidth || document.documentElement.clientWidth || 0;
      const screenWidth = window.screen?.width || 0;
      const candidateWidths = [
        visualViewportWidth,
        layoutViewportWidth,
        screenWidth,
      ].filter((value) => value > 0);
      const effectiveViewportWidth =
        candidateWidths.length > 0 ? Math.min(...candidateWidths) : layoutViewportWidth;

      const isPortrait =
        window.matchMedia?.('(orientation: portrait)').matches ??
        window.innerHeight >= window.innerWidth;

      const mobilePortrait = isPortrait && effectiveViewportWidth <= 1024;
      const narrowContainer = width > 0 && width < 720;

      setIsCompact(mobilePortrait || narrowContainer);
    };

    updateCompact();

    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver((entries) => {
            updateCompact(entries[0]?.contentRect?.width);
          })
        : null;

    observer?.observe(node);

    const handleViewportChange = () => {
      window.requestAnimationFrame(() => updateCompact());
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);
    window.visualViewport?.addEventListener('resize', handleViewportChange);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
      window.visualViewport?.removeEventListener('resize', handleViewportChange);
    };
  }, []);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '0px';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 140)}px`;
    }
  }, [newMessage]);

  useEffect(() => {
    const paths = Array.from(
      new Set(
        messages
          .map((message) => message.attachment_path || '')
          .filter((path) => path && !attachmentUrls[path])
      )
    );

    if (!paths.length) return;

    let mounted = true;

    Promise.all(
      paths.map(async (path) => {
        const { data, error } = await supabase.storage
          .from(CHAT_ATTACHMENT_BUCKET)
          .createSignedUrl(path, 60 * 60);

        return {
          path,
          url: error ? '' : String(data?.signedUrl || ''),
        };
      })
    ).then((results) => {
      if (!mounted) return;
      setAttachmentUrls((current) => {
        const next = { ...current };
        for (const result of results) {
          if (result.url) next[result.path] = result.url;
        }
        return next;
      });
    });

    return () => {
      mounted = false;
    };
  }, [messages, attachmentUrls]);

  useEffect(() => {
    if (userId) {
      setEffectiveUserId(userId);
      return;
    }

    setEffectiveUserId('');
    if (userType === 'traveler') setLoading(false);
  }, [userId, userType]);

  useEffect(() => {
    if (effectiveUserId) initializeChat();
  }, [effectiveUserId, userType, businessId]);

  useEffect(() => {
    const openDirectChat = async () => {
      if (!initialContactId || userType !== 'traveler' || !effectiveUserId || loading) return;
      if (openedOnceRef.current === initialContactId) return;
      openedOnceRef.current = initialContactId;

      const existing = items.find(
        (i) => i.id === initialContactId || i.business_id === initialContactId
      );

      if (existing) {
        setActiveItem(existing);
        return;
      }

      const { data, error } = await supabase
        .from('businesses')
        .select('id, name, owner_id')
        .eq('id', initialContactId)
        .maybeSingle();

      if (error || !data) {
        console.error("Impossible de charger l'entreprise initiale");
        return;
      }

      const tempConv: Conversation = {
        id: data.id,
        name: data.name,
        business_id: data.id,
        traveler_id: effectiveUserId,
        last_message: '',
        updated_at: new Date().toISOString(),
        business_owner_id: data.owner_id || undefined,
      };

      setItems((prev) => [tempConv, ...prev.filter((i) => i.id !== data.id)]);
      setActiveItem(tempConv);
    };

    openDirectChat();
  }, [initialContactId, effectiveUserId, loading, items, userType]);

  const initializeChat = async () => {
    setLoading(true);
    try {
      if (userType === 'traveler') {
        const { data, error } = await supabase
          .from('conversations')
          .select('*, business:businesses(name, id, owner_id)')
          .eq('traveler_id', effectiveUserId)
          .order('updated_at', { ascending: false });

        if (!error && data) {
          const formatted = data.map((c: any) => ({
            ...c,
            name: c.business?.name || 'Partenaire',
            business_id: c.business_id,
            business_owner_id: c.business?.owner_id || undefined,
          }));
          setItems(formatted);
        }
      } else {
        const { data, error } = await supabase
          .from('conversations')
          .select('*')
          .eq('business_id', businessId || effectiveUserId)
          .order('updated_at', { ascending: false });

        if (error) {
          console.error('Erreur récupération conversations :', error);
        } else if (data) {
          const formattedData = data.map((conv: any) => ({
            ...conv,
            name: `Client #${(conv.traveler_id || '').substring(0, 4).toUpperCase()}`,
            traveler_profiles: {
              email: 'Client Anonyme',
              id: conv.traveler_id,
            },
          }));
          setItems(formattedData);
        }
      }
    } catch (err: any) {
      console.error('Erreur Chat:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeItem && effectiveUserId) {
      loadMessages();

      const channel = supabase
        .channel(`chat_active_${activeItem.id}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages',
            filter: `receiver_id=eq.${effectiveUserId}`,
          },
          (payload) => {
            if (activeItem.id && payload.new.conversation_id === activeItem.id) {
              setMessages((prev) => [...prev, payload.new as Message]);

              setItems((prev) =>
                prev
                  .map((item) =>
                    item.id === activeItem.id
                      ? {
                          ...item,
                          last_message: String(payload.new.content || ''),
                          updated_at: String(payload.new.created_at || new Date().toISOString()),
                        }
                      : item
                  )
                  .sort(
                    (a, b) =>
                      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
                  )
              );

              scrollToBottom();
            } else if (!activeItem.id && payload.new.sender_id === activeItem.business_id) {
              loadMessages();
            }
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [activeItem, effectiveUserId]);

  const loadMessages = async () => {
    if (!activeItem || !effectiveUserId) return;

    try {
      let convId = activeItem.id;

      if (userType === 'traveler') {
        const { data: c } = await supabase
          .from('conversations')
          .select('id')
          .eq('traveler_id', effectiveUserId)
          .eq('business_id', activeItem.business_id || activeItem.id)
          .maybeSingle();

        if (c) {
          convId = c.id;
          setActiveItem((prev) => (prev ? { ...prev, id: c.id } : null));
        } else {
          convId = '';
        }
      }

      if (convId) {
        const { data: msgs } = await supabase
          .from('messages')
          .select('*')
          .eq('conversation_id', convId)
          .order('created_at', { ascending: true });

        setMessages(msgs || []);

        if (userType === 'business') {
          await supabase
            .from('messages')
            .update({ is_read: true })
            .eq('conversation_id', convId)
            .eq('receiver_id', effectiveUserId)
            .neq('sender_id', effectiveUserId);
        }
      } else {
        setMessages([]);
      }

      scrollToBottom();
    } catch (e) {
      console.error('Erreur messages:', e);
    }
  };

  const insertEmoji = (emoji: string) => {
    const node = textareaRef.current;
    if (!node) {
      setNewMessage((current) => `${current}${emoji}`);
      return;
    }

    const start = node.selectionStart ?? newMessage.length;
    const end = node.selectionEnd ?? newMessage.length;
    const next = `${newMessage.slice(0, start)}${emoji}${newMessage.slice(end)}`;
    setNewMessage(next);

    requestAnimationFrame(() => {
      node.focus();
      const cursor = start + emoji.length;
      node.setSelectionRange(cursor, cursor);
    });
  };

  const handleAttachmentSelection = (file?: File | null) => {
    if (!file) return;

    if (userType !== 'business') {
      alert('Seul le commerçant peut joindre un document à cette conversation.');
      return;
    }

    if (!CHAT_ATTACHMENT_TYPES.has(file.type)) {
      alert('Format non permis. Utilisez PDF, Word, Excel, JPG ou PNG.');
      return;
    }

    if (file.size > CHAT_ATTACHMENT_MAX_BYTES) {
      alert('La pièce jointe dépasse la limite de 10 Mo.');
      return;
    }

    setPendingAttachment(file);
  };

  const createAttachmentId = () => {
    const cryptoApi = globalThis.crypto;
    if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
      return cryptoApi.randomUUID();
    }

    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}-${Math.random().toString(36).slice(2, 12)}`;
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      (!newMessage.trim() && !pendingAttachment) ||
      !activeItem ||
      !effectiveUserId ||
      uploadingAttachment
    ) return;

    const content = newMessage.trim();
    const attachment = pendingAttachment;
    const fallbackLastMessage = attachment ? `Pièce jointe : ${attachment.name}` : '';
    const conversationPreview = content || fallbackLastMessage;

    setNewMessage('');
    setPendingAttachment(null);
    setShowEmojiPicker(false);

    let uploadedPath = '';

    try {
      let convId = activeItem.id;

      if (userType === 'traveler') {
        const bizId = activeItem.business_id || activeItem.id;

        const { data: ex } = await supabase
          .from('conversations')
          .select('id')
          .eq('traveler_id', effectiveUserId)
          .eq('business_id', bizId)
          .maybeSingle();

        if (ex) {
          convId = ex.id;
        } else {
          const { data: n, error } = await supabase
            .from('conversations')
            .insert([{
              traveler_id: effectiveUserId,
              business_id: bizId,
              last_message: conversationPreview,
            }])
            .select()
            .single();

          if (error) throw error;

          convId = n.id;
          setActiveItem((prev) => (prev ? { ...prev, id: n.id } : null));
          setItems((prev) => {
            if (prev.find((i) => i.id === n.id)) return prev;
            return [
              {
                ...activeItem,
                id: n.id,
                last_message: conversationPreview,
                updated_at: new Date().toISOString(),
              } as Conversation,
              ...prev,
            ];
          });
        }
      }

      const recipientUserId = userType === 'traveler'
        ? (activeItem.business_owner_id || activeItem.business?.owner_id || '')
        : activeItem.traveler_id;

      if (!recipientUserId) throw new Error('message_recipient_missing');

      let attachmentPayload: Partial<Message> = {};

      if (attachment) {
        if (userType !== 'business') {
          throw new Error('merchant_attachment_only');
        }

        setUploadingAttachment(true);

        const safeName = sanitizeFileName(attachment.name);
        uploadedPath = `${convId}/${Date.now()}-${createAttachmentId()}-${safeName}`;

        const { error: uploadError } = await supabase.storage
          .from(CHAT_ATTACHMENT_BUCKET)
          .upload(uploadedPath, attachment, {
            contentType: attachment.type,
            upsert: false,
          });

        if (uploadError) throw uploadError;

        attachmentPayload = {
          attachment_path: uploadedPath,
          attachment_name: attachment.name,
          attachment_mime_type: attachment.type,
          attachment_size: attachment.size,
        };
      }

      const messageContent = content || (attachment ? `Pièce jointe : ${attachment.name}` : '');

      const { error: msgError } = await supabase.from('messages').insert([
        {
          conversation_id: convId,
          sender_id: effectiveUserId,
          receiver_id: recipientUserId,
          content: messageContent,
          ...attachmentPayload,
        },
      ]);

      if (msgError) throw msgError;

      await supabase
        .from('conversations')
        .update({
          last_message: conversationPreview,
          updated_at: new Date().toISOString(),
        })
        .eq('id', convId);

      setMessages((prev) => [
        ...prev,
        {
          id: `temp-${Date.now()}`,
          conversation_id: convId,
          sender_id: effectiveUserId,
          receiver_id: recipientUserId,
          content: messageContent,
          created_at: new Date().toISOString(),
          is_read: false,
          ...attachmentPayload,
        },
      ]);

      setItems((prev) =>
        prev
          .map((item) =>
            item.id === convId || item.id === activeItem.id
              ? {
                  ...item,
                  id: convId,
                  last_message: conversationPreview,
                  updated_at: new Date().toISOString(),
                }
              : item
          )
          .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
      );

      scrollToBottom();
    } catch (err: any) {
      console.error('Erreur envoi:', err);

      if (uploadedPath) {
        try {
          await supabase.storage.from(CHAT_ATTACHMENT_BUCKET).remove([uploadedPath]);
        } catch {
          // best effort cleanup only
        }
      }

      setNewMessage(content);
      setPendingAttachment(attachment || null);
      alert(`Erreur technique : ${err.message}`);
    } finally {
      setUploadingAttachment(false);
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const filteredItems = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return items;

    return items.filter((item) => {
      const haystack = [
        item.name || '',
        item.last_message || '',
        item.traveler_profiles?.email || '',
        item.business?.name || '',
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [items, searchTerm]);

  const groupedMessages = useMemo(() => {
    const groups: Array<{ label: string; messages: Message[] }> = [];

    for (const msg of messages) {
      const label = getDayLabel(msg.created_at);
      const lastGroup = groups[groups.length - 1];

      if (!lastGroup || lastGroup.label !== label) {
        groups.push({ label, messages: [msg] });
      } else {
        lastGroup.messages.push(msg);
      }
    }

    return groups;
  }, [messages]);

  const showListPanel = !activeItem || !isCompact;
  const showChatPanel = !!activeItem || !isCompact;

  if (loading && items.length === 0) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center rounded-none border-0 bg-white/90 backdrop-blur-xl sm:rounded-[28px] sm:border sm:border-slate-200/80" data-commerce-panel="crm-messenger">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-blue-500/20 blur-2xl" />
            <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-600 text-white shadow-2xl">
              <Loader2 className="animate-spin" size={26} />
            </div>
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold text-slate-700">Chargement des discussions</p>
            <p className="mt-1 text-xs text-slate-700">Connexion à Forfy Messenger…</p>
          </div>
        </div>
      </div>
    );
  }

  if (userType === 'traveler' && !effectiveUserId) {
    return (
      <div className="flex h-full min-h-[360px] items-center justify-center bg-slate-50 p-6">
        <div className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <Shield className="mx-auto mb-4 text-indigo-600" size={34} />
          <h3 className="text-xl font-black text-slate-900">Connexion requise</h3>
          <p className="mt-2 text-sm leading-6 text-slate-700">
            Connectez-vous à votre compte Forfeo pour contacter l’établissement
            et retrouver vos échanges en toute sécurité.
          </p>
          <button
            type="button"
            onClick={() => { window.location.href = '/login'; }}
            className="mt-6 rounded-2xl bg-blue-600 px-6 py-3 text-sm font-black text-white transition hover:bg-blue-700"
          >
            Se connecter
          </button>
        </div>
      </div>
    );
  }


  return (
    <div
      ref={containerRef}
      data-forfy-chat-root="true"
      data-forfeo-ui-v218="crm-messenger"
      data-forfy-chat-active={activeItem ? 'true' : 'false'}
      className="relative flex h-full min-h-0 w-full min-w-0 overflow-hidden rounded-none border-0 bg-white shadow-none sm:rounded-[28px] sm:border sm:border-slate-200/80 sm:shadow-[0_20px_60px_rgba(15,23,42,0.14)]"
     data-commerce-panel-v219="crm" data-commerce-v220-panel="crm">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-20 -top-20 h-56 w-56 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="absolute right-[-60px] top-[15%] h-64 w-64 rounded-full bg-violet-500/10 blur-3xl" />
        <div className="absolute bottom-[-80px] left-[20%] h-64 w-64 rounded-full bg-cyan-400/10 blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(255,255,255,0.72),rgba(248,250,252,0.9))]" />
      </div>

      {showListPanel && (
        <div
          data-forfy-chat-panel="list"
          style={{ display: isCompact && activeItem ? 'none' : 'flex' }} className={`relative z-10 flex h-full flex-col border-r border-slate-200/80 ${
            isCompact ? 'w-full min-w-0' : 'w-[320px] min-w-[290px] max-w-[340px]'
          } bg-white backdrop-blur-xl`}
        >
          <div className="border-b border-slate-200/80 px-4 pb-4 pt-4 sm:px-5 sm:pb-5 sm:pt-5">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.22em] text-blue-700">
                  <Sparkles size={12} />
                  Forfy Messenger
                </div>
                <h2 className="text-2xl font-black leading-none tracking-tight text-slate-900 sm:text-[28px]">
                  Discussions
                </h2>
                <p className="mt-2 hidden text-sm text-slate-700 sm:block">
                  Version premium coordonnée avec l’expérience Business Profile.
                </p>
              </div>

              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-600 text-white shadow-lg">
                <MessageSquare size={20} />
              </div>
            </div>

            <div className="relative">
              <Search
                size={17}
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-700"
              />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher une conversation..."
                className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-300 focus:bg-white focus:ring-4 focus:ring-blue-100"
              />
            </div>
          </div>

          <div className="flex items-center justify-between px-5 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-700">
            <span>{filteredItems.length} conversation{filteredItems.length > 1 ? 's' : ''}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-600">
              <Shield size={12} />
              sécurisé
            </span>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3 min-w-0">
            {filteredItems.length === 0 ? (
              <div className="mx-2 mt-4 rounded-[28px] border border-dashed border-slate-200 bg-slate-50/80 p-10 text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-sm">
                  <MessageSquare className="text-slate-700" size={28} />
                </div>
                <p className="text-sm font-bold text-slate-600">
                  {searchTerm ? 'Aucun résultat trouvé.' : 'Aucune discussion active.'}
                </p>
                <p className="mt-2 text-xs leading-5 text-slate-700">
                  {searchTerm
                    ? 'Essaie un autre mot-clé pour retrouver une conversation.'
                    : 'Les nouveaux messages apparaîtront ici automatiquement.'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredItems.map((listItem) => {
                  const isActive =
                    activeItem?.id === listItem.id ||
                    (!!activeItem?.business_id && activeItem.business_id === listItem.business_id);

                  return (
                    <button
                      key={listItem.id}
                      onClick={() => setActiveItem(listItem)}
                      className={`group relative w-full overflow-hidden rounded-[24px] border p-4 text-left transition-all duration-200 ${
                        isActive
                          ? 'border-blue-200 bg-gradient-to-br from-blue-50 via-white to-indigo-50 shadow-[0_12px_30px_rgba(37,99,235,0.12)]'
                          : 'border-transparent bg-white hover:border-slate-200 hover:bg-slate-50 hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`relative flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-sm font-black shadow-sm ${
                            isActive
                              ? 'bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-600 text-white'
                              : 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {getInitials(listItem.name)}
                          <span className="absolute -bottom-1 -right-1 h-3.5 w-3.5 rounded-full border-2 border-white bg-emerald-400" />
                        </div>

                        <div className="min-w-0 flex-1 min-h-0">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="truncate text-sm font-black uppercase tracking-[0.04em] text-slate-900">
                                {listItem.name || 'Inconnu'}
                              </div>
                              <div className="mt-1 flex items-center gap-2 text-[11px] font-semibold text-slate-700">
                                <span>{formatShortDate(listItem.updated_at)}</span>
                                <span className="h-1 w-1 rounded-full bg-slate-300" />
                                <span>{userType === 'traveler' ? 'Partenaire' : 'Client'}</span>
                              </div>
                            </div>

                            <div
                              className={`rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-[0.16em] ${
                                isActive ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'
                              }`}
                            >
                              actif
                            </div>
                          </div>

                          <p className="mt-3 truncate text-[13px] font-medium text-slate-700">
                            {listItem.last_message || 'Nouvelle conversation'}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {showChatPanel && (
        <div
          data-forfy-chat-panel="chat"
          style={{ display: isCompact && !activeItem ? 'none' : 'flex' }}
          className="relative z-10 flex min-w-0 flex-1 flex-col bg-white backdrop-blur-xl min-h-0"
        >
          {activeItem ? (
            <>
              <div className="relative overflow-hidden border-b border-slate-200/80 bg-white px-4 py-4 text-slate-900 shadow-sm sm:px-6">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.28),transparent_30%),radial-gradient(circle_at_top_left,rgba(99,102,241,0.22),transparent_24%)]" />
                <div className="relative flex items-center gap-3">
                  {isCompact && (
                    <button
                      onClick={() => setActiveItem(null)}
                      className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/10 text-slate-900 shadow-sm transition hover:bg-white/15"
                    >
                      <ArrowLeft size={20} />
                    </button>
                  )}

                  <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-slate-900 shadow-lg backdrop-blur-md">
                    {userType === 'traveler' ? <Store size={22} /> : <User size={22} />}
                    <span className="absolute -bottom-1 -right-1 h-3.5 w-3.5 rounded-full border-2 border-slate-200 bg-emerald-400" />
                  </div>

                  <div className="min-w-0 flex-1 min-h-0">
                    <div className="truncate text-[18px] font-black uppercase tracking-[0.03em] text-slate-950">
                      {activeItem.name || 'Conversation'}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-700">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-2.5 py-1 text-emerald-300">
                        <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                        En ligne
                      </span>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-blue-100">
                        <Bot size={12} />
                        espace premium Forfy
                      </span>
                    </div>
                  </div>

                  <button className="hidden h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/10 text-slate-900/80 shadow-sm transition hover:bg-white/15 sm:inline-flex">
                    <MoreHorizontal size={18} />
                  </button>
                </div>
              </div>

              <div className="relative min-h-0 flex-1 overflow-y-auto bg-[linear-gradient(180deg,rgba(248,250,252,0.55),rgba(255,255,255,0.92))] min-w-0 overscroll-contain px-2.5 py-3 sm:px-5 sm:py-5">
                <div className="pointer-events-none absolute inset-0 opacity-[0.55] [background-image:radial-gradient(circle_at_1px_1px,rgba(148,163,184,0.14)_1px,transparent_0)] [background-size:22px_22px]" />

                <div className="relative z-10 mx-auto flex max-w-5xl flex-col gap-5">
                  {messages.length === 0 && (
                    <div className="mx-auto mt-5 max-w-md rounded-[24px] border border-slate-200/80 bg-white/95 p-5 text-center shadow-lg sm:mt-8 sm:p-8">
                      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-600 text-white shadow-xl">
                        <Sparkles size={26} />
                      </div>
                      <p className="text-base font-black text-slate-800">Débutez la conversation</p>
                      <p className="mt-2 text-sm leading-6 text-slate-700">
                        Posez une question, demandez une information, ou lancez le premier
                        message avec style.
                      </p>
                    </div>
                  )}

                  {groupedMessages.map((group) => (
                    <div key={group.label} className="space-y-4">
                      <div className="flex items-center justify-center">
                        <div className="rounded-full border border-slate-200 bg-white/90 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-700 shadow-sm">
                          {group.label}
                        </div>
                      </div>

                      <div className="space-y-3">
                        {group.messages.map((msg) => {
                          const isMine = msg.sender_id === effectiveUserId;
                          const merchantSenderId =
                            userType === 'business'
                              ? effectiveUserId
                              : activeItem.business_owner_id || activeItem.business?.owner_id || '';
                          const isBusinessMessage = Boolean(
                            merchantSenderId && msg.sender_id === merchantSenderId
                          );
                          const senderRole = isBusinessMessage ? 'business' : 'traveler';
                          const senderLabel = isMine
                            ? isBusinessMessage
                              ? 'Vous · Commerçant'
                              : 'Vous · Voyageur'
                            : isBusinessMessage
                              ? activeItem.name || 'Commerçant'
                              : 'Voyageur';
                          const attachmentUrl = msg.attachment_path
                            ? attachmentUrls[msg.attachment_path]
                            : '';

                          return (
                            <div
                              key={`${msg.id}-${msg.created_at}`}
                              className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                            >
                              <div
                                className={`flex max-w-[94%] flex-col sm:max-w-[78%] ${
                                  isMine ? 'items-end' : 'items-start'
                                }`}
                              >
                                <div
                                  className={`relative overflow-hidden rounded-[22px] px-4 py-3.5 text-sm leading-6 shadow-md ${
                                    isBusinessMessage
                                      ? `${
                                          isMine ? 'rounded-br-md' : 'rounded-bl-md'
                                        } bg-gradient-to-br from-emerald-600 via-green-600 to-teal-600 text-white shadow-[0_12px_30px_rgba(5,150,105,0.24)]`
                                      : `${
                                          isMine ? 'rounded-br-md' : 'rounded-bl-md'
                                        } bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-600 text-white shadow-[0_12px_30px_rgba(79,70,229,0.24)]`
                                  }`}
                                >
                                  <div
                                    className={`mb-1.5 text-[10px] font-black uppercase tracking-[0.17em] ${
                                      isBusinessMessage ? 'text-emerald-100' : 'text-blue-100'
                                    }`}
                                  >
                                    {senderLabel}
                                  </div>

                                  <div className="whitespace-pre-wrap break-words">{msg.content}</div>

                                  {msg.attachment_path && (
                                    <div
                                      className={`mt-3 flex min-w-0 items-center gap-3 rounded-2xl border p-3 ${
                                        isBusinessMessage
                                          ? 'border-emerald-200 bg-white/80'
                                          : 'border-white/20 bg-white/10'
                                      }`}
                                    >
                                      <div
                                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                                          isBusinessMessage
                                            ? 'bg-emerald-100 text-emerald-700'
                                            : 'bg-white/15 text-slate-900'
                                        }`}
                                      >
                                        <FileText size={19} />
                                      </div>

                                      <div className="min-w-0 flex-1">
                                        <div className="break-words text-xs font-black">
                                          {msg.attachment_name || 'Document'}
                                        </div>
                                        <div
                                          className={`mt-0.5 text-[10px] font-semibold ${
                                            isBusinessMessage ? 'text-emerald-700/70' : 'text-blue-100'
                                          }`}
                                        >
                                          {humanFileSize(msg.attachment_size)}
                                        </div>
                                      </div>

                                      {attachmentUrl ? (
                                        <a
                                          href={attachmentUrl}
                                          target="_blank"
                                          rel="noreferrer"
                                          className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition ${
                                            isBusinessMessage
                                              ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                              : 'bg-white text-indigo-700 hover:bg-blue-50'
                                          }`}
                                          aria-label="Ouvrir la pièce jointe"
                                        >
                                          <Download size={17} />
                                        </a>
                                      ) : (
                                        <Loader2 className="shrink-0 animate-spin opacity-60" size={17} />
                                      )}
                                    </div>
                                  )}
                                </div>

                                <div className="mt-1.5 flex items-center gap-1.5 px-1 text-[11px] text-slate-700">
                                  <span>{formatTime(msg.created_at)}</span>
                                  {isMine && <CheckCheck size={12} className="opacity-70" />}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  <div ref={messagesEndRef} />
                </div>
              </div>

              <div className="border-t border-slate-200/80 bg-white/95 px-3 py-3 backdrop-blur-xl sm:px-5 sm:py-4">
                <form onSubmit={handleSendMessage} className="mx-auto max-w-5xl relative overflow-visible">
                  <div
                    className={`relative overflow-hidden rounded-[28px] border bg-white shadow-lg transition-all ${
                      composerFocused
                        ? 'border-blue-300 ring-4 ring-blue-100'
                        : 'border-slate-200'
                    }`}
                  >
                    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-300/70 to-transparent" />

                    {pendingAttachment && (
                      <div className="mx-2 mt-2 flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-emerald-950 sm:mx-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                          <FileText size={18} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="break-words text-xs font-black">{pendingAttachment.name}</div>
                          <div className="mt-0.5 text-[10px] font-semibold text-emerald-700">
                            {humanFileSize(pendingAttachment.size)}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setPendingAttachment(null)}
                          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-slate-700 shadow-sm"
                          aria-label="Retirer la pièce jointe"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    )}

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                      className="hidden"
                      onChange={(event) => {
                        handleAttachmentSelection(event.target.files?.[0]);
                        event.currentTarget.value = '';
                      }}
                    />

                    {showEmojiPicker && (
                      <div className="relative z-[130] mx-2 mb-2 grid max-h-[180px] grid-cols-8 gap-1 overflow-y-auto overscroll-contain rounded-2xl border border-slate-200 bg-white p-2 shadow-2xl sm:ml-auto sm:mr-3 sm:w-[330px]">
                        {CHAT_EMOJIS.map((emoji) => (
                          <button
                            key={emoji}
                            type="button"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => {
                              insertEmoji(emoji);
                              setShowEmojiPicker(false);
                            }}
                            className="grid h-9 w-9 place-items-center rounded-xl text-xl transition hover:bg-slate-100"
                            aria-label={`Insérer ${emoji}`}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="flex items-end gap-1.5 p-2 sm:gap-2 sm:p-3">
                      {userType === 'business' && (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 transition hover:bg-emerald-100"
                          aria-label="Joindre un document"
                        >
                          <Paperclip size={18} />
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => setShowEmojiPicker((current) => !current)}
                        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-50 text-amber-600 transition hover:bg-amber-100"
                        aria-label="Ajouter un emoji"
                        aria-expanded={showEmojiPicker}
                      >
                        <Smile size={18} />
                      </button>

                      <textarea
                        ref={textareaRef}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onFocus={() => setComposerFocused(true)}
                        onBlur={() => setComposerFocused(false)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            if (newMessage.trim() || pendingAttachment) {
                              handleSendMessage(e as unknown as React.FormEvent);
                            }
                          }
                        }}
                        placeholder="Écrivez un message à Forfy Messenger..."
                        rows={1}
                        className="min-h-[46px] max-h-[120px] min-w-0 flex-1 resize-none bg-transparent px-1.5 py-2.5 text-sm leading-6 text-slate-700 outline-none placeholder:text-slate-700 sm:px-2 sm:py-3"
                      />

                      <button
                        type="submit"
                        disabled={
                          uploadingAttachment ||
                          (!newMessage.trim() && !pendingAttachment)
                        }
                        className="group relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-600 text-white shadow-[0_12px_24px_rgba(79,70,229,0.24)] transition hover:scale-[1.03] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100 sm:h-12 sm:w-12"
                      >
                        {uploadingAttachment ? (
                          <Loader2 className="animate-spin" size={18} />
                        ) : (
                          <Send size={18} />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="mt-2 hidden items-center justify-between px-2 text-[11px] text-slate-700 sm:flex">
                    <span>Entrée pour envoyer · Maj + Entrée pour une nouvelle ligne</span>
                    <span className="font-medium">{newMessage.length} caractère(s)</span>
                  </div>
                </form>
              </div>
            </>
          ) : (
            <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-[linear-gradient(180deg,rgba(248,250,252,0.65),rgba(255,255,255,0.98))] p-6 min-w-0">
              <div className="pointer-events-none absolute inset-0 opacity-[0.55] [background-image:radial-gradient(circle_at_1px_1px,rgba(148,163,184,0.14)_1px,transparent_0)] [background-size:22px_22px]" />

              <div className="relative z-10 mx-auto max-w-lg rounded-[32px] border border-slate-200 bg-white p-10 text-center shadow-[0_25px_70px_rgba(15,23,42,0.12)] backdrop-blur-xl">
                <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-[24px] bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-600 text-white shadow-2xl">
                  <MessageSquare size={34} />
                </div>

                <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-blue-700">
                  <Sparkles size={12} />
                  Interface conversationnelle
                </div>

                <p className="text-2xl font-black tracking-tight text-slate-900">
                  Sélectionnez une conversation
                </p>
                <p className="mt-3 text-sm leading-7 text-slate-700">
                  Ouvrez une discussion pour voir l’historique complet, répondre rapidement et
                  échanger dans une interface premium pensée pour 2026.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatSystem;
